import {createApi,fetchBaseQuery} from "@reduxjs/toolkit/query/react"

export const CollectionApi = createApi({
    reducerPath: "collectionApi",
    baseQuery: fetchBaseQuery({
        baseUrl:import.meta.env.VITE_API_URL,
        prepareHeaders:(headers)=>{
            const token = localStorage.getItem('_at') || null
            if(token){
                headers.set("Authorization", "Bearer "+token)
            }
            return headers;
        }
    }),
    tagTypes: ['Collection', 'CollectionList', 'CollectionProducts'],
    // Keep unused data for 10 minutes for better performance
    keepUnusedDataFor: 600,
    // Refetch on reconnect for better UX
    refetchOnReconnect: true,
    // Refetch on focus for fresh data when user returns
    refetchOnFocus: true,
    endpoints:(builder)=>({
        listAll: builder.query({
            query: ({ page = 1, limit = 10, search = '' }) => 
              `/collection?page=${page}&limit=${limit}&search=${search}`,
            providesTags: (result, error, { page, search }) => [
                { type: 'CollectionList', id: `${page}-${search}` },
                'Collection'
            ],
            // Keep paginated data for 5 minutes
            keepUnusedDataFor: 300,
            // Transform response to normalize data structure
            transformResponse: (response) => {
                return {
                    ...response,
                    // Add timestamp for cache validation
                    cachedAt: Date.now()
                };
            },
        }),
        createCollection:builder.mutation({
            query:(formData)=> ({
                url: "/collection",
                body:formData,
                method:"POST",
                headers:()=>([
                    {"Content-Type":"multipart/form-data"}
                ])
            }),
            invalidatesTags: ['Collection', 'CollectionList'],
            // Optimistic update for better UX
            onQueryStarted: async (formData, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    // Invalidate all list queries to refresh data
                    dispatch(CollectionApi.util.invalidateTags(['CollectionList']));
                } catch {}
            },
        }),
        showById:builder.query({
            query:(id)=>`/collection/${id}`,
            providesTags: (result, error, id) => [
                { type: 'Collection', id }
            ],
            // Keep individual collection data for 15 minutes
            keepUnusedDataFor: 900,
            // Transform response to add metadata
            transformResponse: (response) => ({
                ...response,
                cachedAt: Date.now()
            }),
        }),
        updateCollection:builder.mutation({
            query:({id,payload})=> ({
                url: `/collection/${id}`,
                body:payload,
                method:"PUT",
                headers:()=>([
                    {"Content-Type":"multipart/form-data"}
                ])
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Collection', id },
                'CollectionList'
            ],
            // Optimistic update for better UX
            onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    // Invalidate specific collection and lists
                    dispatch(CollectionApi.util.invalidateTags([
                        { type: 'Collection', id },
                        'CollectionList'
                    ]));
                } catch {}
            },
        }),
        deleteCollection:builder.mutation({
            query:(id)=>({
                url:`/collection/${id}`,
                method:"DELETE"
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'Collection', id },
                'CollectionList',
                'CollectionProducts'
            ],
            // Optimistic update for better UX
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    // Invalidate all related caches
                    dispatch(CollectionApi.util.invalidateTags([
                        { type: 'Collection', id },
                        'CollectionList',
                        'CollectionProducts'
                    ]));
                } catch {}
            },
        }),
        listForHome:builder.query({
            query:() => "/collection/list",
            // Cache home collections for 30 minutes since they change less frequently
            keepUnusedDataFor: 1800,
            // Provide more specific tags for better cache management
            providesTags: (result) => 
                result?.result?.data
                    ? [
                        ...result.result.data.map(({ _id }) => ({ type: 'Collection', id: _id })),
                        { type: 'Collection', id: 'HOME_LIST' },
                    ]
                    : [{ type: 'Collection', id: 'HOME_LIST' }],
            // Transform response for better performance
            transformResponse: (response) => ({
                ...response,
                cachedAt: Date.now(),
                // Pre-process data for faster rendering
                processedData: response?.result?.data?.map(item => ({
                    ...item,
                    imageUrl: item.image || item.desktopImage || item.mobileImage,
                    hasVideo: !!(item.video || item.desktopVideo || item.mobileVideo)
                }))
            }),
        }),
        listProductFromCollection:builder.query({
            query:(collectionId)=>`/collection/fetchById/${collectionId}`,
            // Keep product collection data for 10 minutes
            keepUnusedDataFor: 600,
            // Provide tags for better cache invalidation
            providesTags: (result, error, collectionId) => [
                { type: 'CollectionProducts', id: collectionId },
                { type: 'Collection', id: collectionId },
            ],
            // Transform response to optimize product data
            transformResponse: (response) => ({
                ...response,
                cachedAt: Date.now(),
                // Pre-process products for faster rendering
                processedProducts: response?.result?.map(product => ({
                    ...product,
                    heroImage: product.media?.find(m => m.type === 'image')?.url,
                    hasVideo: product.media?.some(m => m.type === 'video'),
                    availableSizes: product.sizes?.filter(s => s.quantity > 0).map(s => s.size),
                    totalStock: product.sizes?.reduce((sum, s) => sum + s.quantity, 0) || 0
                }))
            }),
        }),
        
        // Add prefetch methods for better performance
        prefetchHome: builder.query({
            query: () => "/collection/list",
            keepUnusedDataFor: 1800,
            providesTags: [{ type: 'Collection', id: 'HOME_PREFETCH' }],
        }),
        
        prefetchCollection: builder.query({
            query: (id) => `/collection/${id}`,
            keepUnusedDataFor: 900,
            providesTags: (result, error, id) => [
                { type: 'Collection', id: `${id}_PREFETCH` }
            ],
        })
    })
}) 

export const {
    useListAllQuery, 
    useListForHomeQuery,
    useCreateCollectionMutation, 
    useDeleteCollectionMutation, 
    useUpdateCollectionMutation, 
    useShowByIdQuery,
    useListProductFromCollectionQuery,
    // Prefetch hooks for performance optimization
    usePrefetchHomeQuery,
    usePrefetchCollectionQuery,
    // Lazy query hooks for conditional loading
    useLazyListAllQuery,
    useLazyShowByIdQuery,
    useLazyListProductFromCollectionQuery
} = CollectionApi

// Export utility functions for manual cache management
export const {
    prefetch,
    invalidateTags,
    updateQueryData
} = CollectionApi.util